import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import { Button } from '@/common/components/Button.tsx'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/common/components/Card'
import {AppDispatch, RootState} from '@/storage/store'
import {useDispatch, useSelector} from 'react-redux'
import { format, parseISO } from 'date-fns'
import { useEffect, useRef } from 'react'
import { LogUserActivity } from '@/apis/users/requests'
import { LOG_TYPES } from '../../constants'
import {RefreshFamilyApplication, selectFamilyApplication} from "@/storage/applications/familyApplicationSlice";
import {getStudentDetailsStartRoute} from "@/app/pages/family_application/utils";
import {selectProgramID} from "@/storage/program_settings/programSettingsSlice";
import {FamilyApplicationData} from "@/layouts/FamilyApplicationLayout";
import useFamilyApplicationRouting from "@/app/pages/family_application/routing/useFamilyApplicationRouting";
import { ClockIcon } from '@/app/common/icons/general'

const IdentityVerificationInfo = () => {
  const applicantDetails = useOutletContext<FamilyApplicationData>()
  const programId = useSelector(selectProgramID)
  const dispatch = useDispatch<AppDispatch>()
  const location = useLocation();
  const residencyManualData = useSelector(selectFamilyApplication)
  const { onModuleEnd } = useFamilyApplicationRouting(residencyManualData)
  const { t: translate } = useTranslation()
  const navigateTo = useNavigate()
  const FamilyApplicationState = useSelector(
    (state: RootState) => state.FamilyApplication
  )

  const hasLoggedPageView = useRef(false)

  const validate_residency = location.state?.validate_residency || false;

  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Identity Verification Complete Summary.',
      }) // Log page view
      hasLoggedPageView.current = true // Mark as executed
    }
  }, [])

  const continueButton = () => {
    if(validate_residency) {
      navigateTo(AppRoutePaths.family_application.residency_verification.index)
    } else {
      dispatch(RefreshFamilyApplication(programId))
      const nextRoute = getStudentDetailsStartRoute(
          applicantDetails.familyApplication
      )
      navigateTo(onModuleEnd(nextRoute))
    }
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1 className="text-3xl font-medium text-gray-900">
          {translate('identity_verification_complete')}
        </h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="items-center justify-center">
        <ClockIcon className="text-7xl text-primary mb-7" />
        <h3 className="text-xl font-medium text-center text-gray-800 mb-8">
          {translate('identity_verify_complete')}
        </h3>
        <Card className="w-full flex-shrink-0">
          <CardHeader className="font-medium text-md">{translate('information')}</CardHeader>
          <CardContent className="w-full">
            <ul>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">
                  {translate('first_name')}
                </span>
                <span className="text-neutral-900 font-medium">
                  {FamilyApplicationState?.data?.first_name}{' '}
                </span>
              </li>
              {FamilyApplicationState?.data?.middle_name && (
                <li className="flex items-center justify-between py-2">
                  <span className="text-neutral-700">
                    {translate('middle_name')}
                  </span>
                  <span className="text-neutral-900 font-medium">
                    {FamilyApplicationState?.data?.middle_name}
                  </span>
                </li>
              )}
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">
                  {translate('last_name')}
                </span>
                <span className="text-neutral-900 font-medium">
                  {FamilyApplicationState?.data?.last_name}
                </span>
              </li>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">
                  {translate('birth_date')}
                </span>
                <span className="text-neutral-900 font-medium">
                  {FamilyApplicationState?.data?.date_of_birth
                    ? format(
                        parseISO(FamilyApplicationState?.data?.date_of_birth),
                        'MM-dd-y'
                      )
                    : ''}
                </span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar className="flex-col">
        {' '}
        <span className="text-gray-600 mb-2 text-center">
          {
            validate_residency ? translate('up_next_residency') : translate('up_next_student_details')
          }
        </span>{' '}
        <Button
          onClick={continueButton}
          className="h-9 mb-5"
        >
          {translate('continue')}
        </Button>
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
export default IdentityVerificationInfo
